import { useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import Modal from '../../components/Modal';
import Select from '../../components/Select';
import Button from '../../components/Button';
import ErrorMessages from '../../components/ErrorMessages.js';
import usersAPI from '../../api/users';
import parkingAPI from '../../api/parking';
import requsetErrorMessage from '../../helpers/requsetErrorMessage';
import uniqueById from '../../helpers/uniqueById';
import useLockBodyScroll from '../../customHooks/useLockBodyScroll';

// Search an app user and assign the whole parking pass to them (owner),
// mirroring the Abo owner model. Reused from the field pass list.
const userLabel = (u) => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return `${name || u.username || '—'}${u.email ? ` · ${u.email}` : ''} (#${u.id})`;
};

const AssignOwnerModal = ({ passId, currentOwner, onClose, onAssigned }) => {
    const modalRef = useRef(null);
    useLockBodyScroll();
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const getUsers = async () => {
            try {
                const res = await usersAPI.getUsers({
                    params: { page, search: searchQuery, limit: 10 },
                });
                const items = res.data.items || [];
                setUsers((prev) => (page === 1 ? uniqueById(items) : uniqueById([...prev, ...items])));
            } catch (e) {
                console.log(e);
            }
            setLoadingMore(false);
        };
        searchQuery && getUsers();
    }, [page, searchQuery]);

    const searchHandler = debounce((value) => {
        setPage(1);
        setSearchQuery(value);
    }, 500);

    const bottomReached = () => {
        setLoadingMore(true);
        setPage((p) => p + 1);
    };

    const doAssign = async () => {
        if (!selected) return;
        setSaving(true);
        setError(null);
        try {
            await parkingAPI.assignOwner(passId, { user_id: selected.value });
            onAssigned && onAssigned();
        } catch (err) {
            setError(requsetErrorMessage(err && err.message ? err.message : err));
            setSaving(false);
        }
    };

    const doRemove = async () => {
        setSaving(true);
        setError(null);
        try {
            await parkingAPI.unassignOwner(passId);
            onAssigned && onAssigned();
        } catch (err) {
            setError(requsetErrorMessage(err && err.message ? err.message : err));
            setSaving(false);
        }
    };

    const close = () => onClose && onClose();

    const options = users.map((u) => ({ value: u.id, label: userLabel(u) }));

    return (
        <Modal title="Parkkarte zuteilen" ref={modalRef} onClose={close} maxWidth="480px" disabled={saving}>
            {currentOwner && (
                <p style={{ color: '#333', marginBottom: '15px' }}>
                    Aktueller Besitzer: <b>{currentOwner}</b>
                </p>
            )}
            <p style={{ color: '#333', marginBottom: '10px' }}>
                Benutzer suchen (Name oder E-Mail):
            </p>
            <Select
                options={options}
                onInputChange={(value) => searchHandler(value)}
                onChangeCallback={(e) => setSelected(e)}
                bottomReached={bottomReached}
                isLoading={loadingMore}
                isClearable
                hasError={false}
                placeholder="Suchen…"
                noOptionsMsg={searchQuery ? 'Keine Treffer' : 'Tippen zum Suchen'}
            />

            <div style={{ marginTop: '15px' }}>
                <ErrorMessages messages={error || ''} />
            </div>

            <div className="Modal-Controls" style={{ marginTop: '20px' }}>
                <Button
                    label="Zuteilen"
                    loading={saving}
                    disabled={saving || !selected}
                    onClick={doAssign}
                    style={{ width: '120px' }}
                />
                {currentOwner && (
                    <Button
                        label="Entfernen"
                        theme="gray"
                        disabled={saving}
                        onClick={doRemove}
                        style={{ width: '120px', marginLeft: '15px' }}
                    />
                )}
                <Button
                    label="Abbrechen"
                    theme="light"
                    disabled={saving}
                    onClick={close}
                    style={{ width: '120px', marginLeft: '15px' }}
                />
            </div>
        </Modal>
    );
};

export default AssignOwnerModal;
